import { useState, useMemo, useEffect } from "preact/hooks"
import useSWR from "swr"
import { Card, CardContent, CardHeader } from "../../../components/ui/card"
import { ChartContainer } from "../../../components/ui/chart"
import {
  LineChart, Line, CartesianGrid,
  XAxis, YAxis, Tooltip, Legend, ResponsiveContainer
} from "recharts"
import { Button } from "../../../components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { format, parseISO } from "date-fns"
import {
  ChartDataResponse,
  LineChartDataPoint,
  ChartMetric,
} from "../types/distributor_sales"
import { getSkuSummary } from "../api/distributor_sales_api"
import { SkuPieChart } from "./distributor_sales_sku_pie"

interface DistributorSalesChartsProps {
  data: ChartDataResponse | null
  selectedRetailers: string[]
  onRetailersChange: (retailers: string[]) => void
  selectedSkus: string[]
  onSkusChange: (skus: string[]) => void
  skuMode: boolean
  onSkuModeChange: (mode: boolean) => void
  source?: string
  reportId?: string
  className?: string
}

// Color palette for series
const SERIES_COLORS = [
  "#2563eb", "#dc2626", "#16a34a", "#ca8a04", "#9333ea",
  "#0891b2", "#db2777", "#ea580c", "#4f46e5", "#059669",
  "#7c3aed", "#0284c7", "#be123c", "#65a30d", "#c026d3",
]

function currencyFormatter(value: number): string {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`
  return `$${value.toFixed(0)}`
}

function toNumber(value: number | string | undefined): number {
  if (value === undefined) return 0
  if (typeof value === "string") {
    const parsed = parseFloat(value)
    return isNaN(parsed) ? 0 : parsed
  }
  return value
}

function useAvailableMetrics(data: ChartDataResponse | null): ChartMetric[] {
  return useMemo(() => {
    if (!data?.line_chart_data?.length) return []
    const metrics: ChartMetric[] = []
    const check = (key: ChartMetric) => data.line_chart_data.some((p: LineChartDataPoint) => toNumber(p[key] as any) !== 0)
    if (check("ordered_vendor_cost")) metrics.push("ordered_vendor_cost")
    if (check("shipped_vendor_cost")) metrics.push("shipped_vendor_cost")
    if (check("ordered_list_wholesale")) metrics.push("ordered_list_wholesale")
    if (check("shipped_list_wholesale")) metrics.push("shipped_list_wholesale")
    if (check("ordered_quantity")) metrics.push("ordered_quantity")
    if (check("shipped_quantity")) metrics.push("shipped_quantity")
    return metrics
  }, [data?.line_chart_data])
}

export function DistributorSalesCharts({
  data,
  selectedRetailers,
  onRetailersChange,
  selectedSkus,
  onSkusChange,
  skuMode,
  onSkuModeChange,
  source,
  reportId,
  className,
}: DistributorSalesChartsProps) {
  const [showFilter, setShowFilter] = useState(false)
  const [selectedMetric, setSelectedMetric] = useState<ChartMetric>("shipped_vendor_cost")

  const availableMetrics = useAvailableMetrics(data)

  useEffect(() => {
    if (availableMetrics.length > 0 && !availableMetrics.includes(selectedMetric)) {
      setSelectedMetric(availableMetrics[0])
    }
  }, [availableMetrics, selectedMetric])

  // SKU summary (used for the filter list)
  const { data: skuSummary } = useSWR(
    ["sku-summary", source, reportId],
    () => getSkuSummary({ source: source as any, report_id: reportId }),
    { revalidateOnFocus: false }
  )

  // Biggest first, as the server ranks them, so the default ten are the top ten (not the first
  // ten alphabetically).
  const retailers = useMemo(() => {
    if (!data?.line_chart_data) return []
    if (data.available_retailers.length > 0) return data.available_retailers
    return Array.from(new Set(data.line_chart_data.map((p: LineChartDataPoint) => p.retailer)))
  }, [data?.line_chart_data, data?.available_retailers])

  const allRetailersSelected = useMemo(
    () => retailers.length > 0 && selectedRetailers.length === retailers.length,
    [selectedRetailers, retailers]
  )

  const allSkusSelected = useMemo(
    () => (skuSummary?.available_skus.length ?? 0) > 0 && selectedSkus.length === skuSummary?.available_skus.length,
    [selectedSkus, skuSummary]
  )

  // Transform line chart data with client-side filtering
  const lineChartData = useMemo(() => {
    if (!data?.line_chart_data) return []
    const entitiesToShow = skuMode
      ? (selectedSkus.length > 0
        ? selectedSkus
        : (skuSummary?.available_skus.length
          ? skuSummary.available_skus.slice(0, 10)
          : retailers.slice(0, 10)))
      : (selectedRetailers.length > 0 ? selectedRetailers : retailers.slice(0, 10))

    const byDate: Record<string, Record<string, number>> = {}
    data.line_chart_data.forEach((point: LineChartDataPoint) => {
      if (point.retailer === "All Other") return
      const key = point.start_date
      if (!byDate[key]) {
        byDate[key] = {
          start_date: point.start_date,
          end_date: point.end_date,
          date_range: `${format(parseISO(point.start_date), "MMM d")} - ${format(parseISO(point.end_date), "MMM d")}`,
        } as any
      }
      if (!entitiesToShow.includes(point.retailer)) return
      let value = 0
      if (selectedMetric === "ordered_vendor_cost") value = toNumber(point.ordered_vendor_cost)
      else if (selectedMetric === "shipped_vendor_cost") value = toNumber(point.shipped_vendor_cost)
      else if (selectedMetric === "ordered_list_wholesale") value = toNumber(point.ordered_list_wholesale)
      else if (selectedMetric === "shipped_list_wholesale") value = toNumber(point.shipped_list_wholesale)
      else if (selectedMetric === "ordered_quantity") value = point.ordered_quantity || 0
      else if (selectedMetric === "shipped_quantity") value = point.shipped_quantity || 0
      else if (selectedMetric === "stores_ordered") value = 0
      byDate[key][point.retailer] = (byDate[key][point.retailer] || 0) + value
    })

    return Object.values(byDate).sort((a: any, b: any) => a.start_date.localeCompare(b.start_date))
  }, [data?.line_chart_data, selectedMetric, selectedRetailers, retailers, selectedSkus, skuSummary, skuMode])

  const displaySeries = useMemo(() => {
    if (!lineChartData.length) return []
    const keys = new Set<string>()
    lineChartData.forEach((point: any) => {
      Object.keys(point).forEach((k: string) => {
        if (k !== "start_date" && k !== "end_date" && k !== "date_range") keys.add(k)
      })
    })
    return Array.from(keys)
  }, [lineChartData])

  const metricConfig = useMemo(() => {
    if (selectedMetric === "ordered_vendor_cost") return { label: "Ordered (vendor cost)", formatter: currencyFormatter }
    if (selectedMetric === "shipped_vendor_cost") return { label: "Shipped (vendor cost)", formatter: currencyFormatter }
    if (selectedMetric === "ordered_list_wholesale") return { label: "Ordered (list wholesale)", formatter: currencyFormatter }
    if (selectedMetric === "shipped_list_wholesale") return { label: "Shipped (list wholesale)", formatter: currencyFormatter }
    if (selectedMetric === "ordered_quantity") return { label: "Ordered (Qty)", formatter: (v: number) => v.toLocaleString() }
    if (selectedMetric === "stores_ordered") {
      return { label: "Store count", formatter: (v: number) => `${Math.round(v).toLocaleString()} stores` }
    }
    return { label: "Shipped (Qty)", formatter: (v: number) => v.toLocaleString() }
  }, [selectedMetric])

  const chartConfig = useMemo(() => {
    const config: Record<string, { label: string; color: string }> = {}
    displaySeries.forEach((series: string, idx: number) => {
      config[series] = {
        label: series,
        color: SERIES_COLORS[idx % SERIES_COLORS.length],
      }
    })
    return config
  }, [displaySeries])

  function toggleRetailer(retailer: string) {
    const effective = selectedRetailers.length > 0 ? selectedRetailers : retailers.slice(0, 10)
    onRetailersChange(
      effective.includes(retailer)
        ? effective.filter((r: string) => r !== retailer)
        : [...effective, retailer]
    )
  }

  function toggleSku(sku: string) {
    const effective = selectedSkus.length > 0 ? selectedSkus : skuSummary?.available_skus.slice(0, 10) ?? []
    onSkusChange(
      effective.includes(sku)
        ? effective.filter((s: string) => s !== sku)
        : [...effective, sku]
    )
  }

  function toggleSelectAllRetailers() {
    onRetailersChange(allRetailersSelected ? [] : [...retailers])
  }

  function toggleSelectAllSkus() {
    onSkusChange(allSkusSelected ? [] : [...(skuSummary?.available_skus ?? [])])
  }

  const hasTrendData = Boolean(data?.line_chart_data?.length)

  const availableEntities = skuMode
    ? (skuSummary?.available_skus ?? [])
    : (data?.available_retailers ?? [])

  return (
    <div class={`space-y-6 ${className || ""}`}>
      {!hasTrendData ? (
        <div class="text-center text-gray-500 py-4 rounded-lg border border-dashed border-gray-200 bg-gray-50/50">
          <p class="text-sm">
            {source === "mclane"
              ? "No weekly trend data yet. Upload a McLane sales export to see sales trends over time."
              : "No weekly trend data yet. Upload KeHE reports with a 5–9 day date range to see sales trends over time."}
            The SKU breakdown below uses your selected table report.
          </p>
        </div>
      ) : null}

      {/* Line Chart */}
      {hasTrendData ? (
      <Card>
        <CardHeader>
          <div class="flex items-center justify-between flex-wrap gap-4">
            <h3 class="text-lg font-semibold">
              {skuMode ? "Sales Trends by SKU" : "Sales Trends by Retailer"}
            </h3>
            <div class="flex items-center gap-3 flex-wrap">
              {/* View mode toggle */}
              <div class="flex rounded-md border border-gray-200 overflow-hidden text-sm">
                <button
                  class={`px-3 py-1.5 transition-colors ${!skuMode ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                  onClick={() => onSkuModeChange(false)}
                >
                  By Retailer
                </button>
                <button
                  class={`px-3 py-1.5 transition-colors ${skuMode ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                  onClick={() => onSkuModeChange(true)}
                >
                  By SKU
                </button>
              </div>

              {/* Metric selector */}
              <div class="w-44">
                <Select
                  value={selectedMetric}
                  onValueChange={(v: string) => setSelectedMetric(v as ChartMetric)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ordered_vendor_cost">Ordered (vendor cost)</SelectItem>
                    <SelectItem value="ordered_list_wholesale">Ordered (list wholesale)</SelectItem>
                    <SelectItem value="ordered_quantity">Ordered (Qty)</SelectItem>
                    <SelectItem value="shipped_vendor_cost">Shipped (vendor cost)</SelectItem>
                    <SelectItem value="shipped_list_wholesale">Shipped (list wholesale)</SelectItem>
                    <SelectItem value="shipped_quantity">Shipped (Qty)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button variant="outline" size="sm" onClick={() => setShowFilter(!showFilter)}>
                {showFilter ? "Hide" : "Show"} {skuMode ? "SKU" : "Retailer"} Filter
              </Button>
            </div>
          </div>

          {/* Filter panel */}
          {showFilter && (
            <div class="mt-4 space-y-3">
              <div>
                <Button variant="outline" size="sm" onClick={skuMode ? toggleSelectAllSkus : toggleSelectAllRetailers}>
                  {(skuMode ? allSkusSelected : allRetailersSelected) ? "Deselect All" : `Select All ${skuMode ? "SKUs" : "Retailers"}`}
                </Button>
              </div>
              <div class="flex flex-wrap gap-2">
                {availableEntities.slice(0, 40).map((entity: string) => {
                  const effective = skuMode
                    ? (selectedSkus.length > 0 ? selectedSkus : skuSummary?.available_skus.slice(0, 10) ?? [])
                    : (selectedRetailers.length > 0 ? selectedRetailers : retailers.slice(0, 10))
                  const isSelected = effective.includes(entity)
                  return (
                    <button
                      key={entity}
                      onClick={() => skuMode ? toggleSku(entity) : toggleRetailer(entity)}
                      class={`px-2 py-1 text-xs rounded-full border transition-colors ${
                        isSelected
                          ? "bg-blue-100 border-blue-300 text-blue-700"
                          : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {entity.length > 30 ? entity.slice(0, 30) + "…" : entity}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} class="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={lineChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date_range" tickLine={false} axisLine={false} />
                <YAxis yAxisId="left" tickFormatter={metricConfig.formatter} tickLine={false} axisLine={false} />
                <Tooltip
                  content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null
                    const sorted = [...payload].sort((a: any, b: any) => (b.value || 0) - (a.value || 0))
                    let dateLabel = label
                    const point = payload[0]?.payload
                    if (point) {
                      try {
                        dateLabel = `${format(parseISO(point.start_date), "MMM d")} - ${format(parseISO(point.end_date), "MMM d, yyyy")}`
                      } catch { /* fallback to label */ }
                    }
                    return (
                      <div class="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                        <p class="font-semibold text-sm mb-2">{dateLabel}</p>
                        <div class="space-y-1">
                          {sorted.map((entry: any, idx: number) => {
                            const value = typeof entry.value === "number" ? entry.value : parseFloat(entry.value) || 0
                            return (
                              <div key={idx} class="flex items-center justify-between gap-4 text-sm">
                                <div class="flex items-center gap-2">
                                  <div class="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                                  <span class="text-gray-700">{entry.name}</span>
                                </div>
                                <span class="font-medium text-gray-900">{metricConfig.formatter(value)}</span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  }}
                />
                <Legend />
                {displaySeries.map((series: string, idx: number) => (
                  <Line
                    key={series}
                    type="monotone"
                    dataKey={series}
                    yAxisId="left"
                    stroke={chartConfig[series]?.color || SERIES_COLORS[idx % SERIES_COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    name={series}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>
      ) : null}

      {/* SKU Pie Chart — always visible (uses table report when reportId is set) */}
      <SkuPieChart source={source} reportId={reportId} />
    </div>
  )
}

if (import.meta.vitest) {
  describe("DistributorSalesCharts trend gate", () => {
    it("treats empty line_chart_data as no trend data", () => {
      const data: ChartDataResponse = { line_chart_data: [], available_retailers: [] }
      expect(Boolean(data.line_chart_data?.length)).toBe(false)
    })
  })
}
